import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import { queryKeysCompany } from '../queryKeys';

import { fetchCompanyAliasAddresses } from './fetchCompanyAliasAddresses';
import {
  type CompanyAliasAddressesResponse,
  type CompanyAliasAddressesSearchParameters,
} from './types';

/**
 * Generates the query options for the company alias addresses query so they can be shared between the useQuery hook and the prefetching.
 */
export const getCompanyAliasAddressesQueryOptions = ({
  options = {},
  searchParams,
}: {
  options?: Omit<
    UseQueryOptions<CompanyAliasAddressesResponse>,
    'queryKey' | 'queryFn'
  >;
  searchParams: CompanyAliasAddressesSearchParameters;
}): UseQueryOptions<CompanyAliasAddressesResponse> => {
  return {
    queryFn: async () => fetchCompanyAliasAddresses(searchParams),
    queryKey: queryKeysCompany.getAliasAddresses(searchParams),
    staleTime: ms('30s'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting all company alias addresses with given query parameters.
 * Handles pagination and prefetches the next page of company alias addresses for better performance.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_all_company_addresses_filtered_vadmin_company_alias_address_get
 */
export const useQueryCompanyAliasAddresses = (
  searchParams: CompanyAliasAddressesSearchParameters,
  options?: Omit<
    UseQueryOptions<CompanyAliasAddressesResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryClient = useQueryClient();

  const nextPageOffset =
    (searchParams.offset ?? 0) +
    (searchParams.limit ?? PAGINATION_PAGE_SIZE_DEFAULT);

  void queryClient.prefetchQuery(
    getCompanyAliasAddressesQueryOptions({
      options,
      searchParams: {
        ...searchParams,
        offset: nextPageOffset,
      },
    }),
  );

  return useQuery<CompanyAliasAddressesResponse>({
    ...getCompanyAliasAddressesQueryOptions({
      options,
      searchParams,
    }),
    placeholderData: keepPreviousData,
  });
};
